/**
 * Inline alert box for contextual messages. Errors/warnings use `role="alert"`;
 * success/info use `role="status"`. Optionally dismissible.
 */
import type { ReactElement, ReactNode } from 'react';

export type AlertType = 'success' | 'error' | 'warning' | 'info';

export interface AlertProps {
  type: AlertType;
  children: ReactNode;
  title?: string;
  onClose?: () => void;
  className?: string;
}

const styleMap: Record<AlertType, { box: string; icon: string; path: ReactNode }> = {
  success: {
    box: 'bg-green-50 text-green-800 border-green-200',
    icon: 'text-green-500',
    path: <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />,
  },
  error: {
    box: 'bg-red-50 text-red-800 border-red-200',
    icon: 'text-red-500',
    path: <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />,
  },
  warning: {
    box: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    icon: 'text-yellow-500',
    path: <path d="M12 9v4m0 4h.01M10.3 3.9l-8 14A1.5 1.5 0 003.6 20h16.8a1.5 1.5 0 001.3-2.1l-8-14a1.5 1.5 0 00-2.6 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />,
  },
  info: {
    box: 'bg-brand-50 text-brand-800 border-brand-200',
    icon: 'text-brand-500',
    path: <path d="M12 8h.01M11 12h1v4h1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />,
  },
};

/** Contextual alert box. */
export default function Alert({
  type,
  children,
  title,
  onClose,
  className = '',
}: AlertProps): ReactElement {
  const s = styleMap[type];
  const isAssertive = type === 'error' || type === 'warning';

  return (
    <div
      role={isAssertive ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-md border px-4 py-3 text-sm ${s.box} ${className}`.trim()}
    >
      <svg className={`mt-0.5 h-5 w-5 shrink-0 ${s.icon}`} viewBox="0 0 24 24" aria-hidden="true">
        {s.path}
      </svg>
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        <div className={title ? 'mt-0.5' : ''}>{children}</div>
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded p-0.5 opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-current"
          aria-label="Đóng thông báo"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  );
}
